import { useEffect, useState, useMemo } from "react";
import { calculateHackerRankStars } from "../utils/helpers.jsx";
import { Download } from "lucide-react";

// Import components
import LoadingState from "./loading-state";
import StudentHeader from "./student-header";
import LiveSessionAttendance from "./live-session-attendance";
import TestsOverview from "./tests-overview";
import SubjectAccuracy from "./subject-accuracy";
import LearningProgress from "./learning-progress";
import CourseProgress from "./course-progress";
import AssessmentList from "./assessment-list";
import PlatformPerformance from "./platform-performance";
import ReportFooter from "./report-footer";

export default function StudentReport() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [missingParams, setMissingParams] = useState(false);
  const [isTableView, setIsTableView] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const searchParams = new URLSearchParams(window.location.search);
        const candidateId = searchParams.get("candidateId");
        const reportType = searchParams.get("reportType");

        // Check if table view is requested
        if (reportType === "table") {
          setIsTableView(true);
        }

        if (!candidateId) {
          console.warn("Missing required parameters or candidateId");
          setMissingParams(true);
          return;
        }

        const assessmentIds = searchParams.getAll("assessmentIds");
        const courseIds = searchParams.getAll("courseIds");
        const skillAssessmentIds = searchParams.getAll("skillAssessmentIds");

        // Extract live session triplets
        const sessionIds = searchParams.getAll("sessionId");
        const startDates = searchParams.getAll("startDate");
        const endDates = searchParams.getAll("endDate");

        const liveSessions = sessionIds.map((id, idx) => ({
          sessionId: id,
          startDate: startDates[idx],
          endDate: endDates[idx],
        }));

        const params = new URLSearchParams();
        if (candidateId) params.append("candidateId", candidateId);
        if (assessmentIds.length) {
          assessmentIds.forEach((id) => params.append("assessmentIds", id));
        }
        if (courseIds.length) {
          courseIds.forEach((id) => params.append("courseIds", id));
        }
        if (skillAssessmentIds.length) {
          skillAssessmentIds.forEach((id) =>
            params.append("skillAssessmentIds", id)
          );
        }

        // Append live session fields to URL
        liveSessions.forEach(({ sessionId, startDate, endDate }) => {
          params.append("sessionId", sessionId);
          params.append("startDate", startDate);
          params.append("endDate", endDate);
        });

        const url = `${
          import.meta.env.VITE_SERVERLESS_FUNCTION_BASE_URL
        }/client/report/details?${params.toString()}`;

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const responseData = await response.json();
        setData(responseData);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle PDF download
  const handleDownloadPdf = async () => {
    try {
      setPdfDownloading(true);

      // Get current URL
      const currentUrl = window.location.href;

      // Create a filename based on student info
      const filename = data?.fullName
        ? `${data.fullName.replace(/\s+/g, "-")}-report-${
            new Date().toISOString().split("T")[0]
          }.pdf`
        : `student-report-${new Date().toISOString().split("T")[0]}.pdf`;

      // Make POST request to Lambda URL
      const response = await fetch(
        "https://cc3doyofpqveup6cc4jzlvzt340wvctx.lambda-url.ap-south-1.on.aws/client/certificate/certificate/pdf",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            url: currentUrl,
            filename: filename,
            secretKey: "mrt_pdf_2025_s3cr3t_k3y",
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();

      if (responseData.success && responseData.pdfUrl) {
        // Open the PDF URL in a new window/tab to trigger download
        window.open(responseData.pdfUrl, "_blank");
      } else {
        throw new Error("Failed to generate PDF");
      }
    } catch (err) {
      console.error("Error downloading PDF:", err);
      alert("Failed to download PDF. Please try again later.");
    } finally {
      setPdfDownloading(false);
    }
  };

  // Calculate overall metrics from all courses
  const overallStats = useMemo(() => {
    if (!data?.course_bb_participants) return null;

    const courseParticipants = data.course_bb_participants || [];
    let totalCompletedLessons = 0;
    let totalLessons = 0;
    let totalTimeSpent = 0;
    let activeCourses = 0;
    let completedCourses = 0;

    courseParticipants.forEach((participant) => {
      const course = participant.courses_bb;
      const stats = course.course_content_block_stats || {};

      totalCompletedLessons += stats.completed_mandatory_modules || 0;
      totalLessons += stats.total_mandatory_modules || 0;
      totalTimeSpent += course.time_spent || 0;

      if (course.active) {
        activeCourses++;
      }

      // Consider a course completed if percentage is 100%
      if (stats.percent === 100) {
        completedCourses++;
      }
    });

    const overallPercentage =
      totalLessons > 0
        ? Math.round((totalCompletedLessons / totalLessons) * 100)
        : 0;

    // Format time spent in hours and minutes
    const hours = Math.floor(totalTimeSpent / 3600);
    const minutes = Math.floor((totalTimeSpent % 3600) / 60);

    return {
      totalCompletedLessons,
      totalLessons,
      overallPercentage,
      totalTimeSpent,
      formattedTimeSpent: hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`,
      activeCourses,
      completedCourses,
      totalCourses: courseParticipants.length,
    };
  }, [data]);

  // Get current date for report generation
  const currentDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Check if test attempt sub-topic wise data exists
  const hasSubjectData = useMemo(() => {
    // Check both possible data formats
    return (
      data?.user_sub_topic_wise_overview?.test_attempt_sub_topic_wise_overview
        ?.length > 0 || data?.test_attempt_sub_topic_wise_overview?.length > 0
    );
  }, [data]);

  // Format CSS for the container based on report type
  const containerClass = `container mx-auto py-4 md:py-8 px-3 md:px-6 max-w-[1000px] bg-white
     ${
       isTableView
         ? "print:text-sm print:max-w-none print:w-full print:px-2"
         : ""
     }`;

  // Add print-specific styles
  useEffect(() => {
    // Create a style element for print-specific styles
    const styleElement = document.createElement("style");
    styleElement.type = "text/css";
    styleElement.media = "print";

    // CSS optimizations for PDF printing with Puppeteer
    const printStyles = `
      @page { 
        size: A4; 
        margin: 10mm;
      }
      body {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      table {
        width: 100% !important;
        break-inside: auto !important;
        table-layout: fixed !important;
      }
      tr {
        page-break-inside: avoid !important;
        page-break-after: auto !important;
      }
      td, th {
        page-break-inside: avoid !important;
        white-space: normal !important;
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
        max-width: 250px !important;
      }
      th {
        font-weight: bold !important;
        background-color: #f3f4f6 !important;
        color: #111827 !important;
      }
      td, th {
        padding: 6px !important;
        font-size: 11px !important;
        white-space: normal !important;
      }
      h2, h3 {
        page-break-after: avoid !important;
      }
      .overflow-x-auto {
        overflow: visible !important;
      }
      .rounded-full {
        border-radius: 4px !important;
      }
      /* Hide UI controls in print mode */
      .print-hidden {
        display: none !important;
      }
    `;

    styleElement.appendChild(document.createTextNode(printStyles));
    document.head.appendChild(styleElement);

    // Clean up when component unmounts
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Log the available data for debugging
  console.log("Subject data check:", {
    format1:
      data?.user_sub_topic_wise_overview?.test_attempt_sub_topic_wise_overview,
    format2: data?.test_attempt_sub_topic_wise_overview,
    hasSubjectData,
  });

  // Loading, error, and missing parameters states
  if (loading) {
    return <LoadingState />;
  }

  if (missingParams) {
    return (
      <div className="container mx-auto py-8 px-4 max-w-[1000px] bg-white">
        <div className="font-bold text-center text-red-600">
          Missing required parameters:{" "}
          <span className="text-gray-800">candidateId</span>.
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-8 px-4 max-w-[1000px] bg-white">
        Error loading report: {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto py-8 px-4 max-w-[1000px] bg-white">
        No data available
      </div>
    );
  }

  // Get university information
  const universityInfo = {
    name: data.university?.name || "N/A",
    logo: data.university?.icon || null,
  };

  // Get college information
  const collegeInfo =
    data.college_users && data.college_users.length > 0
      ? {
          name: data.college_users[0]?.college?.name || "N/A",
          logo: data.college_users[0]?.college?.logo || null,
        }
      : null;

  // Extract data from the API response
  const student = {
    name: data.fullName || "N/A",
    email: data.email || "N/A",
    batch: data.batch_users?.[0]?.batch?.name || "N/A",
    universityName: data.university?.name || "N/A",
    universityIcon: data.university?.icon || null,
    id: data.id || "N/A",
    avatar: data.profile_picture || "/placeholder.svg?height=100&width=100",
    overview: {
      testAssigned:
        data.user_assessment_summary?.assigned_test?.aggregate?.count || 0,
      testcompleted:
        data.user_assessment_summary?.completed_test?.aggregate?.count || 0,
      progress:
        Math.round(
          (data.user_assessment_summary?.completed_test?.aggregate?.count /
            (data.user_assessment_summary?.assigned_test?.aggregate?.count ||
              1)) *
            100
        ) || 0,
      questionsAttempted:
        data.user_assessment_summary?.test_question_submissions_aggregate
          ?.aggregate?.count || 0,
      correctAnswers:
        data.user_assessment_summary?.correct_submissions?.aggregate?.count ||
        0,
      partialCorrectAnswers:
        data.user_assessment_summary?.partial_correct_submissions?.aggregate
          ?.count || 0,
      incorrectAnswers:
        data.user_assessment_summary?.incorrect_submissions?.aggregate?.count ||
        0,
      averageCorrectPercentage:
        data.user_assessment_summary?.average_correct_percentage || 0,
      timeSpent: data.user_assessment_summary
        ?.test_question_submissions_aggregate?.aggregate?.sum?.time_taken
        ? Math.floor(
            data.user_assessment_summary?.test_question_submissions_aggregate
              ?.aggregate?.sum?.time_taken / 3600
          ) +
          "h " +
          Math.floor(
            (data.user_assessment_summary?.test_question_submissions_aggregate
              ?.aggregate?.sum?.time_taken %
              3600) /
              60
          ) +
          "m"
        : "0h 0m",
    },
  };

  // Extract assignments data from test_participants
  const assessments =
    data.test_participants?.map((participant) => ({
      id: participant.id,
      name: participant.test?.title || "Unknown Assignment",
      submittedOn: participant.test_attempt?.completed_at
        ? new Date(participant.test_attempt.completed_at).toLocaleDateString(
            "en-US",
            {
              year: "numeric",
              month: "long",
              day: "numeric",
            }
          )
        : "N/A",
      percentage: Math.round(
        (participant.test_attempt?.test_attempt_overview?.percentage || 0) * 100
      ),
      status: participant.test_attempt?.is_completed ? "completed" : "pending",
    })) || [];

  // Extract skill assessments data
  const skillAssessments =
    data.skill_assessments?.map((participant) => ({
      id: participant.id,
      name: participant.test?.title || "Unknown Assignment",
      submittedOn: participant.test_attempt?.completed_at
        ? new Date(participant.test_attempt.completed_at).toLocaleDateString(
            "en-US",
            {
              year: "numeric",
              month: "long",
              day: "numeric",
            }
          )
        : "N/A",
      percentage: Math.round(
        (participant.test_attempt?.test_attempt_overview?.percentage || 0) * 100
      ),
      status: participant.test_attempt?.is_completed ? "completed" : "pending",
    })) || [];

  // Extract courses data
  const courses =
    data.course_bb_participants?.map((course) => ({
      id: course.courses_bb.id,
      name: course.courses_bb.name,
      total: course.courses_bb.course_content_block_stats?.total_modules || 0,
      completed:
        course.courses_bb.course_content_block_stats?.completed_modules || 0,
      progress: course.courses_bb.course_content_block_stats?.percent || 0,
    })) || [];

  // Platform performance data
  const platformData = {
    leetcodeLeaderboard: data.leetcodeLeaderboard || [],
    hackerrank_leaderboard: data.hackerrank_leaderboard || [],
    Gfgprofile: data.Gfgprofile || [],
    // codeforcesData: data.codeforcesData || { leaderboard: [] },

    // Keep the old format as fallback for backward compatibility
    leetcode: {
      problemsSolved: data.leetcodeLeaderboard?.[0]?.["All count"] || 0,
      easy: data.leetcodeLeaderboard?.[0]?.["Easy count"] || 0,
      medium: data.leetcodeLeaderboard?.[0]?.["Medium count"] || 0,
      hard: data.leetcodeLeaderboard?.[0]?.["Hard count"] || 0,
      badges:
        data.leetcodeLeaderboard?.[0]?.badge ||
        (data.leetcodeLeaderboard?.[0]?.badges?.length
          ? data.leetcodeLeaderboard?.[0]?.badges?.length
          : 0),
    },
    hackerrank: {
      total_score: Math.round(
        data.hackerrank_leaderboard?.[0]?.total_score || 0
      ),
      stars: calculateHackerRankStars(
        data.hackerrank_leaderboard?.[0]?.total_score || 0
      ),
      algorithms_score: data.hackerrank_leaderboard?.[0]?.algorithms_score || 0,
      algorithms_global_rank:
        data.hackerrank_leaderboard?.[0]?.algorithms_global_rank || 0,
      structures_score: data.hackerrank_leaderboard?.[0]?.structures_score || 0,
      structures_global_rank:
        data.hackerrank_leaderboard?.[0]?.structures_global_rank || 0,
      mathematics_score:
        data.hackerrank_leaderboard?.[0]?.mathematics_score || 0,
      mathematics_global_rank:
        data.hackerrank_leaderboard?.[0]?.mathematics_global_rank || 0,
      badge: data.hackerrank_leaderboard?.[0]?.badge || "",
    },
    codeforcesData: {
      problems_solved:
        data.codeforcesData?.leaderboard?.[0]?.problems_solved || 0,
    },
    gfg: {
      problems_solved: data.Gfgprofile?.[0]?.allCount || 0,
      HardCount: data.Gfgprofile?.[0]?.HardCount || 0,
      MediumCount: data.Gfgprofile?.[0]?.MediumCount || 0,
      EasyCount: data.Gfgprofile?.[0]?.EasyCount || 0,
    },
  };

  return (
    <div className={containerClass}>
      <StudentHeader
        universityInfo={universityInfo}
        collegeInfo={collegeInfo}
        student={student}
        data={data}
      />

      {/* View toggle and PDF Download Button */}
      <div className="flex items-center justify-between mb-4 print-hidden">
        <button
          onClick={handleDownloadPdf}
          disabled={pdfDownloading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-colors bg-blue-600 border border-blue-700 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {pdfDownloading ? (
            <>
              <span className="inline-block w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin"></span>
              Generating PDF...
            </>
          ) : (
            <>
              <Download className="w-4 h-4" />
              Download PDF Report
            </>
          )}
        </button>

        <a
          href={`${window.location.pathname}?${new URLSearchParams({
            ...Object.fromEntries(new URLSearchParams(window.location.search)),
            reportType: isTableView ? "" : "table",
          }).toString()}`}
          className="px-3 py-1 text-sm font-medium text-blue-600 transition-colors bg-blue-100 border border-blue-200 rounded-md hover:bg-blue-200 hover:text-blue-700"
        >
          Switch to {isTableView ? "Card" : "Table"} View
        </a>
      </div>

      <LiveSessionAttendance
        attendance={data?.live_session_attendance}
        isTableView={isTableView}
      />

      <TestsOverview overview={student.overview} isTableView={isTableView} />

      {/* Subject Accuracy Section - Passing the raw API data directly */}
      {/* {hasSubjectData && (
        <SubjectAccuracy
          subjectProgress={
            data?.test_attempt_sub_topic_wise_overview ||
            data?.user_sub_topic_wise_overview
              ?.test_attempt_sub_topic_wise_overview
          }
          isTableView={isTableView}
        />
      )} */}

      <LearningProgress stats={overallStats} isTableView={isTableView} />

      <CourseProgress courses={courses} isTableView={isTableView} />

      <AssessmentList
        assessments={skillAssessments}
        title="Skill Assessments"
        isTableView={isTableView}
      />

      <AssessmentList
        assessments={assessments}
        title="Assessments"
        isTableView={isTableView}
      />

      <PlatformPerformance
        platformData={platformData}
        isTableView={isTableView}
      />

      <ReportFooter
        universityInfo={universityInfo}
        collegeInfo={collegeInfo}
        currentDate={currentDate}
      />
    </div>
  );
}
